(function () {
  const initializeWhopSandbox = (root) => {
    if (!root || root.dataset.calistaWhopReady === 'true') return;
    root.dataset.calistaWhopReady = 'true';

    let variantUrls = {};
    let variantSkus = {};
    try {
      variantUrls = JSON.parse(root.dataset.variantUrls || '{}');
      variantSkus = JSON.parse(root.dataset.variantSkus || '{}');
    } catch (error) {
      console.warn('Whop sandbox checkout links could not be read.', error);
    }

    const button = root.querySelector('[data-whop-checkout-button]');
    const errorMessage = root.querySelector('[data-whop-checkout-error]');
    const uploadStatus = root.querySelector('[data-whop-upload-status]');
    const uploadProgress = root.querySelector('[data-whop-upload-progress]');
    const clearFilesButton = getScopeSafeClearButton(root);
    const bridgeUrl = (root.dataset.bridgeUrl || '').trim().replace(/\/$/, '');
    const copy = {
      uploading: root.dataset.uploadingLabel || 'Uploading photos…',
      saving: root.dataset.uploadSavingLabel || 'Finishing photo upload…',
      ready: root.dataset.uploadReadyLabel || 'Photos ready.',
      countError: root.dataset.uploadCountError || 'Attach up to 4 reference images.',
      clearedNote: root.dataset.uploadClearedNote || 'Your selection was cleared.',
      typeError: root.dataset.uploadTypeError || 'Reference photos must be JPG, PNG, WEBP, HEIC, or GIF images.',
      sizeError: root.dataset.uploadSizeError || 'Each reference photo must be 10 MB or smaller.',
      uploadError: root.dataset.uploadError || 'We could not upload your photos. Check your connection and try again.',
      retryError: root.dataset.uploadRetryError || 'Your photos are not ready yet. Please try checkout again, or remove them to continue without photos.',
      checkoutPreparing: root.dataset.checkoutPreparing || 'Preparing checkout…',
      checkoutError: root.dataset.checkoutError || 'Checkout could not be prepared. Please try again.',
      checkoutIncomplete: root.dataset.checkoutIncomplete || 'Please review the highlighted fields above before continuing.',
    };
    let uploadPromise = null;
    let uploadedAttachments = [];
    let uploadGeneration = 0;
    let activeUploadRequest = null;
    if (!button) return;
    const buttonLabel = button.querySelector('[data-checkout-label]');
    const checkoutLabel = (buttonLabel?.textContent || button.textContent).trim();
    const setButtonLabel = (label) => {
      if (buttonLabel) {
        buttonLabel.textContent = label;
      } else {
        button.textContent = label;
      }
    };

    const setCheckoutAvailable = (available) => {
      if (!available) {
        button.dataset.uploadingFiles = 'true';
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('tabindex', '-1');
        if (!button.hasAttribute('aria-busy')) setButtonLabel(copy.uploading);
        return;
      }
      delete button.dataset.uploadingFiles;
      if (!button.hasAttribute('aria-busy')) {
        button.removeAttribute('aria-disabled');
        button.removeAttribute('tabindex');
        setButtonLabel(checkoutLabel);
      }
    };

    function getScopeSafeClearButton(element) {
      const scope = element.closest('product-info') || document;
      return scope.querySelector('[data-clear-reference-files]');
    }

    const getProductForm = () => {
      const scope = root.closest('product-info') || document;
      // The theme also renders a hidden installment form before the real
      // product form. Resolve the form through an intake field so we never
      // read the wrong form when preparing the Whop session.
      const intakeField = scope.querySelector('[name="properties[Email Address]"]');
      if (intakeField?.form) return intakeField.form;
      return scope.querySelector('form.product-form[action*="/cart/add"]') || scope.querySelector('form[action*="/cart/add"]');
    };

    const getScope = () => root.closest('product-info') || document;

    const getSelectedVariant = () => {
      const selectedVariant = getScope().querySelector('[data-selected-variant]');
      if (!selectedVariant) return null;
      try {
        return JSON.parse(selectedVariant.textContent || '{}');
      } catch {
        return null;
      }
    };

    const getVariantId = () => {
      const selectedVariant = getSelectedVariant();
      if (selectedVariant?.id) return String(selectedVariant.id);
      const form = getProductForm();
      const selected = form?.querySelector('[name="id"]');
      return (
        selected?.value ||
        getScope().querySelector('select[name="id"], input[name="id"]')?.value ||
        root.dataset.defaultVariantId ||
        ''
      );
    };

    const updateCheckoutUrl = () => {
      const url = variantUrls[String(getVariantId())] || Object.values(variantUrls)[0] || '';
      if (url) button.href = url;
    };

    const getField = (form, name) => {
      const associated = Array.from(form?.elements || []).find((field) => field.name === name);
      if (associated) return associated;
      return Array.from(getScope().querySelectorAll('input, textarea, select')).find((field) => field.name === name);
    };

    const ritualEmailPattern = /^[^\s@]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/;
    const ritualFieldIsComplete = (field) => {
      const value = String(field?.value || '').trim();
      if (!value) return false;
      return field.type === 'email' ? ritualEmailPattern.test(value) : true;
    };

    const validateRitualFields = (form) => {
      const fields = Array.from(getScope().querySelectorAll('[data-calista-ritual-required]') || []);
      const invalidFields = fields.filter((field) => !ritualFieldIsComplete(field));

      fields.forEach((field) => {
        const wrapper = field.closest('.calista-intake__field');
        const fieldError = wrapper?.querySelector('.calista-intake__error');
        const invalid = invalidFields.includes(field);
        wrapper?.classList.toggle('has-error', invalid);
        field.toggleAttribute('aria-invalid', invalid);
        if (fieldError) fieldError.hidden = !invalid;
      });

      return invalidFields;
    };

    const getReferenceFiles = () =>
      Array.from(getScope().querySelectorAll('.calista-file-upload__input'))
        .flatMap((input) => Array.from(input.files || []));

    const validateReferenceFiles = (files) => {
      if (files.length > 4) return copy.countError;
      const allowedTypes = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/gif']);
      for (const file of files) {
        if (!allowedTypes.has(String(file.type || '').toLowerCase())) {
          return copy.typeError;
        }
        if (file.size > 10 * 1024 * 1024) return copy.sizeError;
      }
      return '';
    };

    const uploadSelectedFiles = (files) => {
      uploadGeneration += 1;
      const generation = uploadGeneration;
      activeUploadRequest?.abort();
      activeUploadRequest = null;
      uploadedAttachments = [];
      if (!files.length || !bridgeUrl) {
        uploadPromise = null;
        if (uploadStatus) uploadStatus.hidden = true;
        if (uploadProgress) uploadProgress.hidden = true;
        setCheckoutAvailable(true);
        return Promise.resolve([]);
      }

      setCheckoutAvailable(false);
      if (uploadStatus) {
        uploadStatus.textContent = `${copy.uploading} ${files.length} ${files.length === 1 ? 'image' : 'images'}`;
        uploadStatus.hidden = false;
      }
      if (uploadProgress) {
        uploadProgress.value = 0;
        uploadProgress.hidden = false;
      }
      const requestBody = new FormData();
      files.forEach((file) => requestBody.append('attachments', file, file.name));
      uploadPromise = new Promise((resolve) => {
        const request = new XMLHttpRequest();
        activeUploadRequest = request;
        request.open('POST', `${bridgeUrl}/upload-files`);
        request.upload.addEventListener('progress', (event) => {
          if (generation !== uploadGeneration || !event.lengthComputable || !uploadProgress) return;
          const percent = Math.round((event.loaded / event.total) * 100);
          uploadProgress.value = percent;
          if (uploadStatus) {
            uploadStatus.textContent = percent >= 100 ? copy.saving : `${copy.uploading} ${percent}%`;
          }
        });
        request.addEventListener('load', () => {
          if (generation !== uploadGeneration) return resolve(null);
          let payload = {};
          try {
            payload = JSON.parse(request.responseText || '{}');
          } catch {
            payload = {};
          }
          if (request.status < 200 || request.status >= 300 || !Array.isArray(payload.attachments)) {
            const message = payload.error || copy.uploadError;
            uploadPromise = null;
            uploadedAttachments = [];
            if (uploadStatus) uploadStatus.hidden = true;
            if (uploadProgress) uploadProgress.hidden = true;
            setCheckoutAvailable(true);
            if (errorMessage) {
              errorMessage.textContent = message;
              errorMessage.dataset.referenceFileError = 'true';
              errorMessage.hidden = false;
            }
            return resolve(null);
          }
          uploadedAttachments = payload.attachments;
          if (uploadStatus) {
            uploadStatus.textContent = copy.ready;
            uploadStatus.hidden = false;
          }
          if (uploadProgress) {
            uploadProgress.value = 100;
            uploadProgress.hidden = true;
          }
          setCheckoutAvailable(true);
          if (errorMessage?.dataset.referenceFileError === 'true') {
            delete errorMessage.dataset.referenceFileError;
            errorMessage.hidden = true;
          }
          resolve(payload.attachments);
        });
        request.addEventListener('error', () => {
          if (generation !== uploadGeneration) return resolve(null);
          uploadPromise = null;
          uploadedAttachments = [];
          if (uploadStatus) uploadStatus.hidden = true;
          if (uploadProgress) uploadProgress.hidden = true;
          setCheckoutAvailable(true);
          if (errorMessage) {
            errorMessage.textContent = copy.uploadError;
            errorMessage.dataset.referenceFileError = 'true';
            errorMessage.hidden = false;
          }
          resolve(null);
        });
        request.addEventListener('abort', () => resolve(null));
        request.send(requestBody);
      });
      return uploadPromise;
    };

    const updateReferenceFileState = () => {
      const message = validateReferenceFiles(getReferenceFiles());
      if (message) {
        if (errorMessage) {
          errorMessage.textContent = message;
          errorMessage.dataset.referenceFileError = 'true';
          errorMessage.hidden = false;
        }
        return message;
      }

      if (errorMessage?.dataset.referenceFileError === 'true') {
        delete errorMessage.dataset.referenceFileError;
        errorMessage.hidden = true;
      }
      return '';
    };

    const startBridgeCheckout = async (event) => {
      event.preventDefault();
      if (button.dataset.uploadingFiles === 'true') return;

      if (updateReferenceFileState()) return;

      const form = getProductForm();
      const invalidFields = validateRitualFields(form);
      if (invalidFields.length) {
        const firstInvalidField = invalidFields[0];
        const fieldWrapper = firstInvalidField.closest('.calista-intake__field');
        const fieldError = fieldWrapper?.querySelector('.calista-intake__error');
        const fieldLabel = fieldWrapper?.querySelector(':scope > span')?.firstChild?.textContent?.trim();
        if (errorMessage) {
          errorMessage.textContent =
            invalidFields.length === 1
              ? `${fieldLabel || 'Field'}: ${fieldError?.textContent?.trim() || 'Please correct this field.'}`
              : 'Please correct the highlighted fields above.';
          errorMessage.hidden = false;
        }
        firstInvalidField.focus({ preventScroll: true });
        firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      const variantId = getVariantId();
      const selectedVariant = getSelectedVariant();
      const email = getField(form, 'properties[Email Address]')?.value?.trim() || '';
      const customer = getField(form, 'properties[Your Name and Birthdate]')?.value?.trim() || '';
      const people = getField(form, "properties[Target's Name and Birth Date(s)]")?.value?.trim() || '';
      const intention = getField(form, 'properties[Situation / Manifestation]')?.value?.trim() || '';
      const referenceFiles = getReferenceFiles();
      const referenceFileError = validateReferenceFiles(referenceFiles);

      if (!variantId || !email || !customer || !people || !intention || referenceFileError) {
        if (errorMessage) {
          errorMessage.textContent = referenceFileError || copy.checkoutIncomplete;
          errorMessage.hidden = false;
        }
        return;
      }

      // Static Whop links still require the ritual form to be complete. When
      // the bridge is not configured, continue to the selected Whop URL only
      // after the same client-side validation has passed.
      if (!bridgeUrl) {
        window.location.assign(button.href);
        return;
      }

      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
      setButtonLabel(copy.checkoutPreparing);
      if (errorMessage) errorMessage.hidden = true;

      try {
        let attachments = uploadedAttachments;
        if (referenceFiles.length) attachments = await (uploadPromise || uploadSelectedFiles(referenceFiles));
        if (referenceFiles.length && (!attachments || attachments.length !== referenceFiles.length)) {
          throw new Error(copy.retryError);
        }
        const requestBody = {
          product_handle: root.dataset.productHandle || '',
          shopify_variant_id: variantId,
          shopify_sku: selectedVariant?.sku || variantSkus[String(variantId)] || '',
          ritual: { email, customer, people, intention },
          attachments,
        };

        const response = await fetch(`${bridgeUrl}/checkout-session`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(requestBody),
        });
        const payload = await response.json();
        if (!response.ok || !payload.checkoutUrl) throw new Error(payload.error || copy.checkoutError);
        window.location.assign(payload.checkoutUrl);
      } catch (error) {
        if (errorMessage) {
          errorMessage.textContent = error.message || copy.checkoutError;
          errorMessage.hidden = false;
        }
        setButtonLabel(checkoutLabel);
        button.removeAttribute('aria-busy');
        button.removeAttribute('aria-disabled');
      }
    };

    updateCheckoutUrl();
    updateReferenceFileState();
    button.addEventListener('click', startBridgeCheckout);
    document.addEventListener('change', (event) => {
      const fileInput = event.target.closest('.calista-file-upload__input');
      if (fileInput) {
        const selectedFiles = getReferenceFiles();
        if (selectedFiles.length > 4) {
          getScope().querySelectorAll('.calista-file-upload__input').forEach((input) => {
            input.value = '';
            input.closest('.calista-file-upload__control')?.classList.remove('has-file');
            const filename = input.parentElement.querySelector('.calista-file-upload__name');
            if (filename) filename.textContent = input.dataset.emptyLabel || 'No photos selected';
          });
          if (errorMessage) {
            errorMessage.textContent = `${copy.countError} ${copy.clearedNote}`;
            errorMessage.dataset.referenceFileError = 'true';
            errorMessage.hidden = false;
          }
          if (clearFilesButton) clearFilesButton.hidden = true;
          uploadSelectedFiles([]);
        } else {
          const fileError = updateReferenceFileState();
          if (clearFilesButton) clearFilesButton.hidden = selectedFiles.length === 0;
          if (!fileError) {
            uploadSelectedFiles(selectedFiles);
          }
        }
      }
      if (event.target.closest('product-info') && (event.target.name === 'id' || event.target.closest('.product-form__input'))) {
        window.setTimeout(updateCheckoutUrl, 0);
      }
    });
    clearFilesButton?.addEventListener('click', () => {
      getScope().querySelectorAll('.calista-file-upload__input').forEach((input) => {
        input.value = '';
        input.closest('.calista-file-upload__control')?.classList.remove('has-file');
        const filename = input.parentElement.querySelector('.calista-file-upload__name');
        if (filename) filename.textContent = input.dataset.emptyLabel || 'No photos selected';
      });
      clearFilesButton.hidden = true;
      uploadSelectedFiles([]);
      if (errorMessage?.dataset.referenceFileError === 'true') {
        delete errorMessage.dataset.referenceFileError;
        errorMessage.hidden = true;
      }
    });
  };

  const boot = () => document.querySelectorAll('[data-calista-whop-sandbox]').forEach(initializeWhopSandbox);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
